import {
  Button,
  Modal,
  Spin,
  Checkbox,
  message,
  Select
} from 'antd';

import { 
  // useCallback,
  useRef, 
  useMemo,
  useState,
  useEffect
} from 'react';

import { useParams } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import JsPdf from 'jspdf';

import { toJpeg } from 'html-to-image';

import {
  imageCompress
} from '../../../libs/imageConversion';

import useModal from '../../../hooks/useModal';

import spuServices from '../../../services/spu'
import useSpuPoints from "../../../stores/spuPoints";
import useSpuPdfParam from "../../../stores/spuPdfParam";

import {
  getImageInfo,
  dataURLtoBlob,
  readBlobAsDataURL,
  parseSpuPointsDataset,
} from '../../../utils';
import {
  getDisplayUnitLabel,
  convertFlowFromCanonical,
  convertHeadFromCanonical,
  normalizeDisplayUnits,
  convertPowerFromCanonical,
  convertNpshrFromCanonical
} from '../../SpuChart/displayUnits/converters';
import {
  buildDisplayDiameter,
  buildPerformanceFormDisplayValues
} from '../../SpuChart/displayUnits/adapter';
import { solveViscousAnchoredCurve } from '../../SpuChart/viscosity/corrections';

import PrintTemplateOne from './modules/PrintTemplateOne';
import PrintTemplateTwo from './modules/PrintTemplateTwo';
import PrintTemplateThree from './modules/PrintTemplateThree';

import "./printViewPdf.css";

interface PrintPdfProps {
  buttonType: "primary" | "default";
}

const PrintViewPdf = ({ buttonType = "primary" }: PrintPdfProps) => { 
  const { t } = useTranslation();

  const { 
    visible, 
    show, 
    onOk, 
    onCancel, 
    okText, 
    cancelText
  } = useModal();

  const { id } = useParams() as { id: string};
  const [searchParams] = useSearchParams();

  const { info, performance, dataset }: any = useSpuPoints();
  const { fieldsValue, fieldsResult } = performance;

  const {
    images: spuChatData
  } = useSpuPdfParam();

  const [printFileName, setPrintFileName] = useState('');
  const [loading, setLoading] = useState(false);
  const [innerBorder, setInnerBorder] = useState(false);
  const [outerBorder, setOuterBorder] = useState(false);
  const [erpId, setErpId] = useState(0);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [resultData, setResultData] = useState({});
  const [chatDataParams, setChatDataParams] = useState({});
  const [templateValue, setTemplateValue] = useState('1');

  const userErpID = useMemo(() => {
    try {
      const userDataStr = localStorage.getItem('aikonLoggedUser');
      if (userDataStr) {
        const userData = JSON.parse(userDataStr);
        return userData.erpID;
      }
    } catch (error) {
    }
    return null;
  }, []);

  useEffect(() => {
    if (userErpID === 13 || userErpID === 14) {
      setTemplateValue('3');
    }
  }, [userErpID]);

  const printTemplateOneRef = useRef<any>(null);
  const printTemplateTwoRef = useRef<any>(null);
  const printTemplateThreeRef = useRef<any>(null);

  const getChatData = () => {
    const displayUnits = normalizeDisplayUnits({
      flow: searchParams.get("flowUnit"),
      head: searchParams.get("headUnit"),
      power: searchParams.get("powerUnit"),
      npshr: searchParams.get("npshrUnit"),
      diameter: searchParams.get("diameterUnit"),
    });
    const {
      quantity,
      head,
      type
    } = fieldsValue || {};
    const displayFormValues = buildPerformanceFormDisplayValues(
      {
        quantity,
        head
      },
      displayUnits
    );

    const {
      range
    } = info || {};
    
    let rangeStr = '';
    
    if (range && range.end !== undefined && range.start !== undefined) {
      const displayRangeStart = convertFlowFromCanonical(range.start, displayUnits.flow);
      const displayRangeEnd = convertFlowFromCanonical(range.end, displayUnits.flow);
      rangeStr = `${Number(displayRangeStart).toFixed(2)} ~ ${Number(displayRangeEnd).toFixed(2)}`;
    }

    const {
      eff,
      frequency,
      power,
      speed,
      diameter,
      diameterUnit,
      npsh
    } = fieldsResult || {};

    const { QH } = parseSpuPointsDataset(dataset);
    
    let effVal = eff !== undefined ? eff.toFixed(2) : '';

    let frequencyVal = frequency !== undefined ? frequency.toFixed(2) : '';
    let speedVal = speed !== undefined ? speed.toFixed(0) : '';

    const displayDiameter = buildDisplayDiameter(diameter, diameterUnit, displayUnits);
    const displayPower = convertPowerFromCanonical(power, displayUnits.power);
    const displayNpsh = convertNpshrFromCanonical(npsh, displayUnits.npshr);
    let diameterVal =
      displayDiameter.value !== undefined && displayDiameter.value !== null
        ? Number(displayDiameter.value).toFixed(0)
        : '';
    let npshVal =
      displayNpsh !== undefined && displayNpsh !== null
        ? Number(displayNpsh).toFixed(2)
        : '';
    let rangeEnd =
      range !== undefined && range.end !== undefined
        ? Number(convertFlowFromCanonical(range.end, displayUnits.flow)).toFixed(2)
        : '';
    let hmVal: any = '';
    
    if (quantity !== undefined && head !== undefined) {
      const center = (range.start + range.end) / 2;
      const solvedCurve = solveViscousAnchoredCurve({
        quantity,
        head,
        type,
        qhPoints: QH.dummyPoints,
        qeffPoints: [],
        qnpshPoints: [],
        qhEquation: QH.equation,
        center,
      });

      if (solvedCurve) {
        const qH2Data = solvedCurve.viscousCurves.qH2v;
        if (qH2Data && qH2Data.length > 0) {
          let hmNum = convertHeadFromCanonical(qH2Data[0][1], displayUnits.head) as number;
          hmVal = hmNum.toFixed(2);
        }
      }
    }

    let chatBase64Data = '';

    if (spuChatData) {
      const {
        performance = ''
      } = spuChatData;

      chatBase64Data = performance;
    }

    const chatData: any = {
      quantity:
        displayFormValues.quantity !== undefined ? Number(displayFormValues.quantity).toFixed(1) : '',
      head:
        displayFormValues.head !== undefined ? Number(displayFormValues.head).toFixed(1) : '',
      hmVal,
      effVal,
      npshVal,
      diameterVal,
      diameterUnitLabel: displayDiameter.unit,
      flowUnitLabel: getDisplayUnitLabel("flow", displayUnits.flow),
      headUnitLabel: getDisplayUnitLabel("head", displayUnits.head),
      powerUnitLabel: getDisplayUnitLabel("power", displayUnits.power),
      npshrUnitLabel: getDisplayUnitLabel("npshr", displayUnits.npshr),
      rangeEnd,
      powerVal:
        displayPower !== undefined && displayPower !== null
          ? Number(displayPower).toFixed(2)
          : '',
      frequencyVal,
      speedVal,
      rangeStr,
      chatBase64Data
    };

    console.log(chatData)

    setChatDataParams(chatData);
  }

  const handleExportPDF = (type?: string) => {
    let pageRefList: Array<any> = [];
    let printFileName = '';

    if (templateValue === '1' && printTemplateOneRef.current && printTemplateOneRef.current.pageRefs && printTemplateOneRef.current.pageRefs.length > 0) {
      pageRefList = printTemplateOneRef.current.pageRefs || [];

      if (printTemplateOneRef.current.getFileName !== undefined) {
        printFileName = printTemplateOneRef.current.getFileName();
      }
    }

    if (templateValue === '2' && printTemplateTwoRef.current && printTemplateTwoRef.current.pageRefs && printTemplateTwoRef.current.pageRefs.length > 0) {
      pageRefList = printTemplateTwoRef.current.pageRefs || [];

      if (printTemplateTwoRef.current.getFileName !== undefined) {
        printFileName = printTemplateTwoRef.current.getFileName();
      }
    }

    if (templateValue === '3' && printTemplateThreeRef.current && printTemplateThreeRef.current.pageRefs && printTemplateThreeRef.current.pageRefs.length > 0) {
      pageRefList = printTemplateThreeRef.current.pageRefs || [];

      if (printTemplateThreeRef.current.getFileName !== undefined) {
        printFileName = printTemplateThreeRef.current.getFileName();
      }
    }

    if (pageRefList.length === 0) {
      return;
    }

    if (printFileName) {
      setPrintFileName(printFileName);
    }
    
    const firstNode = pageRefList[0];

    if (firstNode) {
      setLoading(true);
      getImageList(0, pageRefList, type);
    }
  }

  const filterFunction = (domNode: any) => {
    // 过滤加载失败的图片
    if (domNode.tagName?.toLowerCase() === 'img') {
      const imgElement = domNode;
      // 判断条件：浏览器已尝试加载但失败（complete 为 true）且有效宽度为 0
      if (imgElement.complete && imgElement.naturalWidth === 0) {
        return false; // 将此 img 节点排除
      }
    }

    // 默认返回 true，包含所有未排除的节点
    return true;
  };

  let pdfImageList: Array<any> = [];
  
  const getImageList = async (index: number, pageRefItems: Array<any>, type?: string, node?: any) => {
    const total: number = pageRefItems.length - 1;

    if (index > total) {
      createPDF();

      return;
    }

    let el: any = pageRefItems[index];
    let ratio = window.devicePixelRatio + 1;

    if (type === 'HD') {
      ratio += 1;
    }

    const options = {
      filter: filterFunction,
      backgroundColor: '#ffffff',
      pixelRatio: ratio,
      imagePlaceholder: '',
      skipAutoScale: false,
      cacheBust: true
    };

    toJpeg(el, options).then(async (res: any) => {
      if (res) {
        let imgUrlText = res;

        getImageInfo(imgUrlText, async (imgInfo: any) => {
          if (type === 'HD') {
            pdfImageList.push({
              data: imgUrlText,
              type: imgInfo.width > imgInfo.height ? 'l' : 'p'
            });
  
            getImageList(index + 1, pageRefItems, type);
          } else {
            let imgBlog = dataURLtoBlob(imgUrlText);
            let res = await imageCompress(imgBlog, 220);
  
            readBlobAsDataURL(res, (item: any) => {
              getImageInfo(imgUrlText, async (cimgInfo: any) => {
                pdfImageList.push({
                  data: item,
                  type: cimgInfo.width > cimgInfo.height ? 'l' : 'p'
                });
  
                getImageList(index + 1, pageRefItems, type);
              });
            });
          }
        });
      }
    }).catch((err) => {
      console.log("index error:", err);

      message.error(t("exPdf.message.error"));
      setLoading(false);
    });
  }

  // 生成pdf
  const createPDF = () => {
    const pdf = new JsPdf();
    
    // A4大小，210mm x 297mm，四边不保留边距
    let a4w = 210;
    let a4h = 297;

    pdfImageList.forEach((imgItem: any, index: number) => {
      pdf.addImage(
        imgItem.data,
        'JPEG',
        0,
        0,
        imgItem.type === 'l' ? a4h : a4w, 
        imgItem.type === 'l' ? a4w : a4h
      );

      if (index < pdfImageList.length - 1) {
        let nextItem = pdfImageList[index + 1];
        pdf.addPage("", nextItem.type);
      }
    });

    let fileName = t("exPdf.fileName");

    if (erpId === 13 && printFileName) {
      fileName += printFileName;
    }

    pdf.save(fileName + '.pdf');
    setLoading(false);
  }

  const handleTemplateChange = (value: string) => {
    // 模板1和3基本差不多
    setTemplateValue(value);

    if (value === '2') {
      setInnerBorder(true);
      setOuterBorder(true);
    } else {
      setInnerBorder(false);
      setOuterBorder(false);
    }

    getData(value);
  }

  const getData = async (templateType: string = '1') => {
    try {
      setLoading(true);

      const {
        data: res
      } = await spuServices.getPdfPreviewData(Number(id), Number(templateType));

      setLoading(false);
      
      if (res) {
        const {
          response
        } = res;

        if (response) {
          const {
            erpID
          } = response;

          setErpId(erpID);

          // setResultData(response)

          if (templateType === '1') {
            printTemplateOneRef.current?.setData(response);
          } else if (templateType === '2') {
            printTemplateTwoRef.current?.setData(response);
          } else if (templateType === '3') {
            printTemplateThreeRef.current?.setData(response);
          }
        }
      }
    } catch (error) {
      console.log('error', error);
      
      setLoading(false);
    }
  }

  const handleOpenModal = () => {
    if (userErpID === 13 || userErpID === 14) {
      setTemplateValue('3');
      getData('3');
    } else {
      setTemplateValue('1');
      getData();
    }
    getChatData();
    show();
  };

  const templateOptions = useMemo(() => {
    if (userErpID === 13 || userErpID === 14) {
      return [
        {
          value: '3',
          label: t("exPdf.template.two")
        }
      ];
    }
    return [
      {
        value: '1',
        label: t("exPdf.template.one"),
      },
      {
        value: '3',
        label: t("exPdf.template.two")
      }
    ];
  }, [userErpID, t]);

  return (
    <>
      <Button type={buttonType} onClick={handleOpenModal}>
        { t("exPdf.legacyButton") }
      </Button>
      <Modal
        open={visible}
        title={[
          <span className='modal-title'>{t("exPdf.title")}</span>,
          <Select
            value={templateValue}
            defaultValue={userErpID === 13 || userErpID === 14 ? '3' : '1'}
            style={{ width: 120 }}
            disabled={loading}
            onChange={handleTemplateChange}
            options={templateOptions}
          />
        ]}
        width={'90vw'}
        centered
        bodyStyle={{
          padding: "0",
          height: "82vh",
          background: "#E2E8F0"
        }}
        onOk={onOk} 
        onCancel={onCancel} 
        okText={okText}
        cancelText={cancelText}
        footer={[
          <Checkbox 
            checked={outerBorder}
            disabled={loading}
            onChange={(e: any) => setOuterBorder(e.target.checked)}
          >
            { t("exPdf.outBorder") }
          </Checkbox>,
          <Checkbox 
            checked={innerBorder} 
            disabled={loading}
            onChange={(e: any) => setInnerBorder(e.target.checked)}
          >
            { t("exPdf.innerBorder") }
          </Checkbox>,
          <Button 
            key="standard" 
            type="primary"
            loading={loading}
            onClick={() => handleExportPDF('stand')}
          >
            { t("exPdf.confirmZip") }
          </Button>,
          <Button 
            key="original" 
            type="primary"
            loading={loading}
            onClick={() => handleExportPDF('HD')}
          >
            { t("exPdf.confirmOriginal") }
          </Button>,
        ]}
      >
        <Spin wrapperClassName="export-spin" spinning={loading}>
          <div className="print-layout">
            <div className={`right-content right-content-${erpId}`}>
              {
                resultData && templateValue === '1' ? <PrintTemplateOne
                  ref={printTemplateOneRef}
                  resultData={resultData}
                  chatData={chatDataParams}
                  outerBorder={outerBorder}
                  innerBorder={innerBorder}
                /> : null
              }
              {
                resultData && templateValue === '2' ? <PrintTemplateTwo
                  ref={printTemplateTwoRef}
                  resultData={resultData}
                  chatData={chatDataParams}
                  outerBorder={outerBorder}
                  innerBorder={innerBorder}
                /> : null
              }
              {
                resultData && templateValue === '3' ? <PrintTemplateThree
                  ref={printTemplateThreeRef}
                  resultData={resultData}
                  chatData={chatDataParams}
                  outerBorder={outerBorder}
                  innerBorder={innerBorder}
                /> : null
              }
            </div>
          </div>
        </Spin>
      </Modal>
    </>
  )
}

export default PrintViewPdf;
